import Fuse, { FuseOptionKey } from 'fuse.js';
import { DateTime } from 'luxon';
import { createSortConfig } from '@/utils/sort';
import type {
  TransactionWithBillingDetailsDTO,
  TransactionWithProductsDTO,
} from '@/api/trafikportalen';
import type { TransactionListItemData } from '@/components/Reports/TransactionListItem';
import type { SearchFilter } from '@/types/filterSearch';

export const mapProductTransactionToListItem = (
  dto: TransactionWithProductsDTO,
  departmentsMap?: Record<string, string>,
  organizationsMap?: Record<string, string>,
): TransactionListItemData => {
  const { transaction, transaction_product: product } = dto;

  return {
    id: transaction.id,
    transaction_product_id: product.id,
    created_at: transaction.created_at,
    title: product.title,
    subtitle: product.subtitle ?? '',
    count: product.count,
    source: transaction.source,
    amount_inc_vat: transaction.amount_inc_vat,
    amount_ex_vat: transaction.amount_ex_vat,
    vat_amount: transaction.amount_inc_vat - transaction.amount_ex_vat,
    state: transaction.state,
    department_name: departmentsMap?.[transaction.department_id] ?? '',
    organization_name: organizationsMap?.[transaction.organization_id] ?? '',
    refund_transaction_id: transaction.refund_transaction_id,
    bobcat_order_id: transaction.bobcat_order_id,
    bobcat_transaction_id: transaction.bobcat_transaction_id,
  };
};

export const mapBillingTransactionToListItem = (
  dto: TransactionWithBillingDetailsDTO,
): TransactionListItemData => {
  const {
    transaction,
    transaction_product: product,
    organization,
    department,
  } = dto;

  return {
    id: transaction.id,
    transaction_product_id: product.id,
    created_at: transaction.created_at,
    title: product.title,
    subtitle: product.subtitle ?? '',
    count: product.count,
    source: transaction.source,
    amount_inc_vat: transaction.amount_inc_vat,
    amount_ex_vat: transaction.amount_ex_vat,
    vat_amount: transaction.amount_inc_vat - transaction.amount_ex_vat,
    state: transaction.state,
    department_name: department.name ?? '',
    organization_name: organization.name ?? '',
    refund_transaction_id: transaction.refund_transaction_id,
    bobcat_order_id: transaction.bobcat_order_id,
    bobcat_transaction_id: transaction.bobcat_transaction_id,
  };
};

const SEARCH_KEYS: (keyof TransactionListItemData)[] = [
  'title',
  'department_name',
  'organization_name',
  'id',
  'bobcat_order_id',
  'bobcat_transaction_id',
];

const SORT_CONFIGS = createSortConfig<TransactionListItemData>({
  textFields: ['created_at'],
});

const TRANSACTION_FUSE_OPTIONS = {
  keys: SEARCH_KEYS as FuseOptionKey<TransactionListItemData>[],
  threshold: 0.1,
  distance: 10000,
};

/** Purchase / state / date filters applied after text search and sort (e.g. `sortedItems` from `useSearchFilterSort`). */
export const applyTransactionListFacetFilters = (
  sortedItems: TransactionListItemData[],
  filterSearch: SearchFilter,
): TransactionListItemData[] => {
  const orderPurchaseMethod = filterSearch.orderPurchaseMethod ?? [];
  const state = filterSearch.state ?? [];

  const isPurchaseMethodFiltered = Boolean(orderPurchaseMethod[0]);
  const isStateFiltered = Boolean(state[0]);
  const isDateFromFiltered =
    Boolean(filterSearch.dateFrom) && filterSearch.dateFrom !== '';
  const isDateToFiltered =
    Boolean(filterSearch.dateTo) && filterSearch.dateTo !== '';

  if (
    !(
      isPurchaseMethodFiltered ||
      isStateFiltered ||
      isDateFromFiltered ||
      isDateToFiltered
    )
  ) {
    return sortedItems;
  }

  return sortedItems.filter((transaction) => {
    const matchesPurchaseMethod =
      !isPurchaseMethodFiltered ||
      transaction.source === orderPurchaseMethod[0]?.id;
    const matchesState = !isStateFiltered || transaction.state === state[0]?.id;

    const transactionDate = DateTime.fromISO(transaction.created_at);
    const fromDate = isDateFromFiltered
      ? DateTime.fromISO(filterSearch.dateFrom!).startOf('day')
      : null;
    const toDate = isDateToFiltered
      ? DateTime.fromISO(filterSearch.dateTo!).endOf('day')
      : null;

    const matchesDateFrom =
      !isDateFromFiltered || (fromDate !== null && transactionDate >= fromDate);
    const matchesDateTo =
      !isDateToFiltered || (toDate !== null && transactionDate <= toDate);

    return (
      matchesPurchaseMethod && matchesDateFrom && matchesDateTo && matchesState
    );
  });
};

export const getFilteredSortedTransactionListItems = (
  items: TransactionListItemData[],
  filterSearch: SearchFilter | null,
): TransactionListItemData[] => {
  if (!items.length) {
    return [];
  }

  if (!filterSearch) {
    return items;
  }

  const searchResult = !filterSearch.query
    ? items
    : new Fuse(items, TRANSACTION_FUSE_OPTIONS)
        .search(filterSearch.query)
        .map((result) => result.item);

  const sortKey = filterSearch.sort as keyof typeof SORT_CONFIGS;
  const sortFn = SORT_CONFIGS[sortKey];

  const sortedItems = !sortFn
    ? searchResult
    : [...searchResult].sort((a, b) =>
        filterSearch.direction === 'desc' ? -1 * sortFn(a, b) : sortFn(a, b),
      );

  return applyTransactionListFacetFilters(sortedItems, filterSearch);
};
